import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin,
  Globe,
  Phone,
  Mail,
  Calendar,
  Users,
  Building2,
  Award,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
  Target,
  Eye,
  Heart,
  Sparkles,
  CheckCircle,
  Star,
  ArrowRight,
  Clock,
  Ticket,
  ExternalLink,
  X,
  Info,
} from "lucide-react";
import { IOrganizationProps } from "../../../../types/Organization";
import { IEventProps } from "../../../../types/Event";
import { usePerformRequest } from "../../../../lib/usePerformRequest";
import { DashboardEndpoints } from "../../../../lib/endpoints-dashboard";
import { Loader } from "../../../../components/ui/loader";
import "./styles.scss";

interface HomepageThemeOneProps {
  organizationId: number;
  primaryColor?: string;
  secondaryColor?: string;
}

const HomepageThemeOne: React.FC<HomepageThemeOneProps> = ({
  organizationId,
  primaryColor = "#17265a",
  secondaryColor = "#FFFFFF",
}) => {
  const [selectedEvent, setSelectedEvent] = useState<IEventProps | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Fetch organization data
  const organizationEndpoint = useMemo(() => {
    return DashboardEndpoints.GetSingleOrganization(organizationId);
  }, [organizationId]);

  const { data: orgResponse, isLoading: isLoadingOrg } =
    usePerformRequest<IOrganizationProps>({
      method: organizationEndpoint.method as any,
      route: organizationEndpoint.route,
    });

  const organization = useMemo(() => {
    if (!orgResponse) return null;
    const data = (orgResponse as any)?.data || orgResponse;
    return data as IOrganizationProps;
  }, [orgResponse]);

  // Fetch events data
  const eventsEndpoint = useMemo(() => {
    if (!organizationId) return null;
    return DashboardEndpoints.GetOrganizationEvents(organizationId);
  }, [organizationId]);

  const { data: eventsResponse, isLoading: isLoadingEvents } =
    usePerformRequest<IEventProps[]>({
      method: eventsEndpoint?.method as any,
      route: eventsEndpoint?.route || "",
    });

  const events = useMemo(() => {
    if (!eventsResponse) {
      console.log("No eventsResponse");
      return [];
    }

    console.log("eventsResponse:", eventsResponse);

    // Handle different response structures
    let data = eventsResponse;

    // Check if it's a paginated response
    if ((eventsResponse as any)?.data) {
      data = (eventsResponse as any).data;
    }

    // Check if data is an array
    if (Array.isArray(data)) {
      console.log("Events found as array:", data.length);
      return data as IEventProps[];
    }

    // Check if events are nested in an object
    if (data && typeof data === "object" && (data as any).events) {
      const eventsArray = Array.isArray((data as any).events)
        ? ((data as any).events as IEventProps[])
        : [];
      console.log("Events found in data.events:", eventsArray.length);
      return eventsArray;
    }

    // Check if it's a single event wrapped in an array
    if (data && typeof data === "object" && !Array.isArray(data)) {
      console.log("Single event found, wrapping in array");
      return [data] as IEventProps[];
    }

    console.log("No events found in response structure");
    return [];
  }, [eventsResponse]);

  // All hooks must be called before any early returns
  const socialLinks = useMemo(() => {
    if (!organization) return [];
    return [
      { icon: Facebook, url: organization.facebook_link, label: "Facebook" },
      { icon: Instagram, url: organization.instagram_link, label: "Instagram" },
      { icon: Linkedin, url: organization.linkedin_link, label: "LinkedIn" },
      { icon: Twitter, url: organization.x_link, label: "Twitter" },
      { icon: Youtube, url: organization.youtube_link, label: "YouTube" },
    ].filter((link) => link.url);
  }, [organization]);

  const stats = useMemo(() => {
    if (!organization) return [];
    return [
      {
        icon: Calendar,
        value: (organization as any).event_count?.toLocaleString() || "0",
        label: "Events Hosted",
      },
      {
        icon: Users,
        value: `${
          (organization as any).event_attendees_count?.toLocaleString() || "0"
        }+`,
        label: "Total Attendees",
      },
      {
        icon: Building2,
        value: organization.number_of_locations?.toString() || "0",
        label: "Locations",
      },
      {
        icon: Award,
        value: `${organization.years_of_experience?.toString() || "0"}+`,
        label: "Years of Excellence",
      },
    ];
  }, [organization]);

  const aboutValues = useMemo(() => {
    if (!organization) return [];
    const about = organization.about as any;
    return [
      {
        icon: Target,
        title: "Our Mission",
        description: about?.mission || "",
      },
      {
        icon: Eye,
        title: "Our Vision",
        description: about?.vision || "",
      },
      {
        icon: Heart,
        title: "Our Values",
        description: about?.values || "",
      },
    ].filter((value) => value.description);
  }, [organization]);

  const locationString = useMemo(() => {
    if (!organization?.location) return "";
    const parts = [
      organization.location.address,
      organization.location.city,
      organization.location.state,
      organization.location.country,
    ].filter(Boolean);
    return parts.join(", ");
  }, [organization?.location]);

  if (isLoadingOrg || !organization) {
    return (
      <div className="homepage-theme-one-container">
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            backgroundColor: "#ffffff",
          }}
        >
          <Loader size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="homepage-theme-one-container"
      style={
        {
          "--theme-primary-color": primaryColor,
          "--theme-secondary-color": secondaryColor,
        } as React.CSSProperties
      }
    >
      {/* Hero Section with Cover Image */}
      <section className="hero-section">
        <div className="hero-cover">
          {organization.logo ? (
            <img
              src={organization.logo}
              alt={`${organization.name} cover`}
              className="cover-image"
            />
          ) : (
            <div className="cover-placeholder" />
          )}
          <div className="cover-overlay" />

          {/* Animated Background Elements */}
          <div className="hero-background-elements">
            <motion.div
              className="hero-orb hero-orb-1"
              animate={{
                scale: [1, 1.2, 1],
                x: [0, 50, 0],
                y: [0, -30, 0],
              }}
              transition={{
                duration: 15,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <motion.div
              className="hero-orb hero-orb-2"
              animate={{
                scale: [1, 1.3, 1],
                x: [0, -40, 0],
                y: [0, 40, 0],
              }}
              transition={{
                duration: 18,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 2,
              }}
            />
            <motion.div
              className="hero-orb hero-orb-3"
              animate={{
                scale: [1, 1.15, 1],
                x: [0, 30, 0],
                y: [0, 50, 0],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 4,
              }}
            />
          </div>
        </div>

        <div className="hero-content">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="hero-main"
            >
              {/* Logo */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0, rotate: -10 }}
                animate={{ scale: 1, opacity: 1, rotate: 0 }}
                transition={{ delay: 0.3, duration: 0.6, type: "spring" }}
                className="hero-logo-wrapper"
                whileHover={{ scale: 1.05, rotate: 2 }}
              >
                {organization.logo ? (
                  <img
                    src={organization.logo}
                    alt={`${organization.name} logo`}
                    className="hero-logo"
                  />
                ) : (
                  <div className="hero-logo-placeholder">
                    {organization.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.8, duration: 0.5, type: "spring" }}
                  className="verified-badge"
                  whileHover={{ scale: 1.1, rotate: 360 }}
                >
                  <CheckCircle className="verified-icon" />
                </motion.div>
              </motion.div>

              {/* Name and Tagline */}
              <div className="hero-text">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="hero-title-wrapper"
                >
                  <h1 className="hero-title">{organization.name}</h1>
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.9, duration: 0.8, ease: "easeOut" }}
                    className="hero-title-underline"
                  />
                </motion.div>
                {organization.subtitle && (
                  <motion.p
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                    className="hero-subtitle"
                  >
                    {organization.subtitle}
                  </motion.p>
                )}
                {organization.description && (
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.6 }}
                    className="hero-description"
                  >
                    {organization.description}
                  </motion.p>
                )}
              </div>
            </motion.div>

            {/* Quick Stats in Hero */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="hero-quick-stats"
            >
              {stats.slice(0, 2).map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.8 + index * 0.1, duration: 0.4 }}
                    whileHover={{ scale: 1.05, y: -5 }}
                    className="hero-stat-item"
                  >
                    <div className="hero-stat-icon-wrapper">
                      <Icon className="hero-stat-icon" />
                    </div>
                    <div className="hero-stat-content">
                      <div className="hero-stat-value">{stat.value}</div>
                      <div className="hero-stat-label">{stat.label}</div>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Info Bar */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8, duration: 0.6 }}
        className="info-bar"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="info-bar-content">
            {/* Contact and Social */}
            <div className="info-contact">
              <div className="contact-links">
                {locationString && (
                  <motion.div
                    whileHover={{ scale: 1.02, y: -2 }}
                    className="contact-item contact-item-default"
                  >
                    <MapPin className="contact-icon" />
                    <span>{locationString}</span>
                  </motion.div>
                )}
                {organization.website && (
                  <motion.a
                    whileHover={{ scale: 1.02, y: -2 }}
                    href={organization.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-item contact-item-default"
                  >
                    <Globe className="contact-icon" />
                    <span>Website</span>
                  </motion.a>
                )}
                {organization.phone_number && (
                  <motion.a
                    whileHover={{ scale: 1.02, y: -2 }}
                    href={`tel:${organization.phone_number}`}
                    className="contact-item contact-item-primary"
                  >
                    <Phone className="contact-icon contact-icon-white" />
                    <span>{organization.phone_number}</span>
                  </motion.a>
                )}
              </div>

              {/* Social Links */}
              {socialLinks.length > 0 && (
                <div className="social-links-wrapper">
                  <div className="social-links">
                    {socialLinks.map((social, index) => {
                      const Icon = social.icon;
                      return (
                        <motion.a
                          key={index}
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={social.label}
                          initial={{ opacity: 0, scale: 0 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.9 + index * 0.1 }}
                          whileHover={{ scale: 1.1, y: -3 }}
                          whileTap={{ scale: 0.95 }}
                          className="social-link"
                        >
                          <Icon className="social-icon" />
                        </motion.a>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="stats-background">
          <motion.div
            className="stats-orb stats-orb-1"
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 90, 0],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear",
            }}
          />
          <motion.div
            className="stats-orb stats-orb-2"
            animate={{
              scale: [1, 1.3, 1],
              rotate: [0, -90, 0],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "linear",
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.15,
                  delayChildren: 0.2,
                },
              },
            }}
            className="stats-grid"
          >
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  variants={{
                    hidden: { opacity: 0, y: 30 },
                    visible: {
                      opacity: 1,
                      y: 0,
                      transition: {
                        duration: 0.6,
                        ease: "easeOut",
                      },
                    },
                  }}
                  className="stat-card"
                >
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                    className="stat-icon-wrapper"
                  >
                    <div className="stat-icon-bg">
                      <Icon className="stat-icon" />
                    </div>
                  </motion.div>
                  <motion.div
                    className="stat-value"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    viewport={{ once: true }}
                    transition={{
                      type: "spring",
                      stiffness: 200,
                      damping: 15,
                      delay: index * 0.15 + 0.4,
                    }}
                  >
                    {stat.value}
                  </motion.div>
                  <div className="stat-label">{stat.label}</div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      {(organization.about?.about || aboutValues.length > 0) && (
        <section className="about-section">
          <div className="about-background">
            <motion.div
              className="about-orb about-orb-1"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.5, 0.3],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <motion.div
              className="about-orb about-orb-2"
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.3, 0.5, 0.3],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </div>

          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="about-header"
            >
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="about-badge"
              >
                <Sparkles className="about-badge-icon" />
                <span>Who We Are</span>
              </motion.div>
              <h2 className="about-title">About Us</h2>
              {(organization.about as any)?.about && (
                <p className="about-description">
                  {(organization.about as any).about}
                </p>
              )}
              {!organization.about?.about && organization.description && (
                <p className="about-description">{organization.description}</p>
              )}
            </motion.div>

            {aboutValues.length > 0 && (
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={{
                  hidden: { opacity: 0 },
                  visible: {
                    opacity: 1,
                    transition: {
                      staggerChildren: 0.2,
                      delayChildren: 0.2,
                    },
                  },
                }}
                className="about-values-grid"
              >
                {aboutValues.map((value, index) => {
                  const Icon = value.icon;
                  return (
                    <motion.div
                      key={index}
                      variants={{
                        hidden: { opacity: 0, y: 50 },
                        visible: {
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.6,
                            ease: "easeOut",
                          },
                        },
                      }}
                      className="about-value-card"
                    >
                      <motion.div
                        whileHover={{ y: -6 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                        className="value-card-inner"
                      >
                        <div className="value-card-content">
                          <div className="value-card-accent" />
                          <div className="value-card-body">
                            <div className="value-icon-container">
                              <motion.div
                                whileHover={{ rotate: 360 }}
                                transition={{ duration: 0.6 }}
                                className="value-icon-wrapper"
                              >
                                <Icon className="value-icon" />
                              </motion.div>
                            </div>
                            <div className="value-text-content">
                              <h3 className="value-title">{value.title}</h3>
                              <p className="value-description">
                                {value.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </div>
        </section>
      )}

      {/* Location Details Section */}
      {organization.location && (
        <section className="location-section">
          <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="location-content"
            >
              <div className="location-header">
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="location-icon-wrapper"
                >
                  <MapPin className="location-header-icon" />
                </motion.div>
                <h2 className="location-title">Our Location</h2>
                <p className="location-subtitle">Find us at our headquarters</p>
              </div>
              <div className="location-details-grid">
                {organization.location.address && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 }}
                    className="location-card"
                  >
                    <div className="location-card-icon">
                      <MapPin className="location-card-icon-svg" />
                    </div>
                    <div className="location-card-content">
                      <div className="location-card-label">Address</div>
                      <div className="location-card-value">
                        {organization.location.address}
                      </div>
                    </div>
                  </motion.div>
                )}
                {(organization.location.city ||
                  organization.location.state) && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 }}
                    className="location-card"
                  >
                    <div className="location-card-icon">
                      <Building2 className="location-card-icon-svg" />
                    </div>
                    <div className="location-card-content">
                      <div className="location-card-label">City & State</div>
                      <div className="location-card-value">
                        {organization.location.city}
                        {organization.location.state &&
                          `, ${organization.location.state}`}
                      </div>
                    </div>
                  </motion.div>
                )}
                {organization.location.country && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 }}
                    className="location-card"
                  >
                    <div className="location-card-icon">
                      <Globe className="location-card-icon-svg" />
                    </div>
                    <div className="location-card-content">
                      <div className="location-card-label">Country</div>
                      <div className="location-card-value">
                        {organization.location.country}
                      </div>
                    </div>
                  </motion.div>
                )}
                {organization.location.postal_code && (
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6 }}
                    className="location-card"
                  >
                    <div className="location-card-icon">
                      <Award className="location-card-icon-svg" />
                    </div>
                    <div className="location-card-content">
                      <div className="location-card-label">Postal Code</div>
                      <div className="location-card-value">
                        {organization.location.postal_code}
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Events Section */}
      <section className="events-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="events-header"
          >
            <div className="events-header-content">
              <Calendar className="events-header-icon" />
              <h2 className="events-title">Our Events</h2>
              <p className="events-subtitle">
                Discover our upcoming and past events
              </p>
            </div>
          </motion.div>

          {events.length > 0 ? (
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.15,
                    delayChildren: 0.2,
                  },
                },
              }}
              className="events-grid"
            >
              {events.slice(0, 6).map((event, index) => {
                const eventDate = new Date(event.date);
                const formattedDate = eventDate.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                });
                const formattedTime = eventDate.toLocaleTimeString("en-US", {
                  hour: "2-digit",
                  minute: "2-digit",
                });

                return (
                  <motion.div
                    key={event.id}
                    variants={{
                      hidden: { opacity: 0, y: 50 },
                      visible: {
                        opacity: 1,
                        y: 0,
                        transition: {
                          duration: 0.6,
                          ease: "easeOut",
                        },
                      },
                    }}
                    className="event-card"
                  >
                    <motion.div
                      whileHover={{ scale: 1.02, y: -4 }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className="event-card-inner"
                    >
                      <div className="event-card-main">
                        {event.cover_image ? (
                          <div className="event-card-image-container">
                            <img
                              src={event.cover_image}
                              alt={event.name}
                              className="event-card-image"
                            />
                            <div className="event-image-overlay" />
                          </div>
                        ) : (
                          <div className="event-card-image-placeholder">
                            <Calendar className="event-placeholder-icon" />
                          </div>
                        )}

                        <div className="event-card-body">
                          <div className="event-card-top">
                            <div className="event-type-tag">{event.type}</div>
                            <div
                              className={`event-pricing-badge ${
                                event.is_paid_event ? "paid" : "free"
                              }`}
                            >
                              {event.is_paid_event ? "Paid" : "Free"}
                            </div>
                          </div>

                          <h3 className="event-card-title">{event.name}</h3>

                          <div className="event-card-meta">
                            <div className="event-meta-item">
                              <Calendar className="event-meta-icon" />
                              <div className="event-meta-content">
                                <span className="event-meta-label">Date</span>
                                <span className="event-meta-value">
                                  {formattedDate}
                                </span>
                              </div>
                            </div>
                            {event.location && (
                              <div className="event-meta-item">
                                <MapPin className="event-meta-icon" />
                                <div className="event-meta-content">
                                  <span className="event-meta-label">
                                    Location
                                  </span>
                                  <span className="event-meta-value">
                                    {event.location.city ||
                                      event.location.address ||
                                      "TBA"}
                                  </span>
                                </div>
                              </div>
                            )}
                            <div className="event-meta-item">
                              <Users className="event-meta-icon" />
                              <div className="event-meta-content">
                                <span className="event-meta-label">Seats</span>
                                <span className="event-meta-value">
                                  {event.number_of_seats}
                                </span>
                              </div>
                            </div>
                          </div>

                          {event.event_description && (
                            <p className="event-card-excerpt">
                              {event.event_description.length > 100
                                ? `${event.event_description.substring(
                                    0,
                                    100
                                  )}...`
                                : event.event_description}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="event-card-actions">
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => {
                            setSelectedEvent(event);
                            setIsModalOpen(true);
                          }}
                          className="event-view-more-btn"
                        >
                          <Info className="event-view-more-icon" />
                          <span>View More</span>
                        </motion.button>
                      </div>
                    </motion.div>
                  </motion.div>
                );
              })}
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="events-empty-state"
            >
              <Calendar className="events-empty-icon" />
              <p className="events-empty-text">
                {isLoadingEvents
                  ? "Loading events..."
                  : "No events available at the moment."}
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="footer-content">
            <div className="footer-main">
              <div className="footer-brand">
                {organization.logo ? (
                  <img
                    src={organization.logo}
                    alt={`${organization.name} logo`}
                    className="footer-logo"
                  />
                ) : (
                  <div className="footer-logo-placeholder">
                    {organization.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="footer-brand-info">
                  <h3 className="footer-org-name">{organization.name}</h3>
                  {organization.subtitle && (
                    <p className="footer-org-tagline">
                      {organization.subtitle}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="footer-links">
              <div className="footer-column">
                <h4 className="footer-column-title">Contact</h4>
                <div className="footer-column-links">
                  {organization.email && (
                    <a
                      href={`mailto:${organization.email}`}
                      className="footer-link"
                    >
                      <Mail className="footer-link-icon" />
                      <span>{organization.email}</span>
                    </a>
                  )}
                  {organization.phone_number && (
                    <a
                      href={`tel:${organization.phone_number}`}
                      className="footer-link"
                    >
                      <Phone className="footer-link-icon" />
                      <span>{organization.phone_number}</span>
                    </a>
                  )}
                  {organization.website && (
                    <a
                      href={organization.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="footer-link"
                    >
                      <Globe className="footer-link-icon" />
                      <span>Visit Website</span>
                    </a>
                  )}
                </div>
              </div>

              {organization.location && (
                <div className="footer-column">
                  <h4 className="footer-column-title">Location</h4>
                  <div className="footer-column-links">
                    {locationString && (
                      <div className="footer-link">
                        <MapPin className="footer-link-icon" />
                        <span>{locationString}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-social">
              {socialLinks.length > 0 && (
                <div className="footer-social-links">
                  {socialLinks.map((social, index) => {
                    const Icon = social.icon;
                    return (
                      <motion.a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={social.label}
                        whileHover={{ scale: 1.1, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                        className="footer-social-link"
                      >
                        <Icon className="footer-social-icon" />
                      </motion.a>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="footer-copyright">
              <p>
                © {new Date().getFullYear()} {organization.name}. All rights
                reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Event Details Modal */}
      <AnimatePresence mode="wait">
        {isModalOpen && selectedEvent && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="event-modal-overlay"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, x: "-50%", y: "-50%" }}
              animate={{ opacity: 1, scale: 1, x: "-50%", y: "-50%" }}
              exit={{ opacity: 0, scale: 0.9, x: "-50%", y: "-50%" }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="event-modal"
              style={{
                position: "fixed",
                top: "50%",
                left: "50%",
              }}
            >
              <div className="event-modal-content">
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="event-modal-close"
                >
                  <X className="event-modal-close-icon" />
                </button>

                {selectedEvent.cover_image && (
                  <div className="event-modal-image">
                    <img
                      src={selectedEvent.cover_image}
                      alt={selectedEvent.name}
                    />
                  </div>
                )}

                <div className="event-modal-body">
                  <div className="event-modal-header">
                    <div className="event-modal-badges">
                      <span className="event-modal-type-badge">
                        {selectedEvent.type}
                      </span>
                      <span
                        className={`event-modal-pricing-badge ${
                          selectedEvent.is_paid_event ? "paid" : "free"
                        }`}
                      >
                        {selectedEvent.is_paid_event
                          ? "Paid Event"
                          : "Free Event"}
                      </span>
                    </div>
                    <h2 className="event-modal-title">{selectedEvent.name}</h2>
                  </div>

                  <div className="event-modal-info">
                    <div className="event-modal-info-item">
                      <Calendar className="event-modal-info-icon" />
                      <div className="event-modal-info-content">
                        <span className="event-modal-info-label">Date</span>
                        <span className="event-modal-info-value">
                          {new Date(selectedEvent.date).toLocaleDateString(
                            "en-US",
                            {
                              weekday: "long",
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )}
                        </span>
                      </div>
                    </div>

                    {selectedEvent.location && (
                      <div className="event-modal-info-item">
                        <MapPin className="event-modal-info-icon" />
                        <div className="event-modal-info-content">
                          <span className="event-modal-info-label">
                            Location
                          </span>
                          <span className="event-modal-info-value">
                            {selectedEvent.location.address &&
                              `${selectedEvent.location.address}, `}
                            {selectedEvent.location.city &&
                              `${selectedEvent.location.city}, `}
                            {selectedEvent.location.state &&
                              `${selectedEvent.location.state}, `}
                            {selectedEvent.location.country}
                          </span>
                        </div>
                      </div>
                    )}

                    <div className="event-modal-info-item">
                      <Users className="event-modal-info-icon" />
                      <div className="event-modal-info-content">
                        <span className="event-modal-info-label">
                          Available Seats
                        </span>
                        <span className="event-modal-info-value">
                          {selectedEvent.number_of_seats} seats
                        </span>
                      </div>
                    </div>

                    {selectedEvent.host_name && (
                      <div className="event-modal-info-item">
                        <Award className="event-modal-info-icon" />
                        <div className="event-modal-info-content">
                          <span className="event-modal-info-label">Host</span>
                          <span className="event-modal-info-value">
                            {selectedEvent.host_name}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  {selectedEvent.event_description && (
                    <div className="event-modal-description">
                      <h3 className="event-modal-description-title">
                        About This Event
                      </h3>
                      <p className="event-modal-description-text">
                        {selectedEvent.event_description}
                      </p>
                    </div>
                  )}

                  <div className="event-modal-actions">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setIsModalOpen(false)}
                      className="event-modal-cancel-btn"
                    >
                      Cancel
                    </motion.button>
                    <motion.a
                      href={`/event/${selectedEvent.id}`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="event-modal-view-btn"
                    >
                      <span>View Full Details</span>
                      <ArrowRight className="event-modal-view-icon" />
                    </motion.a>
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HomepageThemeOne;
